import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Transactions from "./pages/Transactions";
import Treasury from "./pages/Treasury";
import LPAccounts from "./pages/LPAccounts";
import PSPs from "./pages/PSPs";
import Vendors from "./pages/Vendors";
import VendorDashboard from "./pages/VendorDashboard";
import Partners from "./pages/Partners";
import PartnerDetail from "./pages/PartnerDetail";
import Reports from "./pages/Reports";
import DailyPnL from "./pages/DailyPnL";
import Settings from "./pages/Settings";
import RolesPermissions from "./pages/RolesPermissions";
import AccountantDashboard from "./pages/AccountantDashboard";
import IncomeExpenses from "./pages/IncomeExpenses";
import Loans from "./pages/Loans";
import BorrowerDetail from "./pages/BorrowerDetail";
import Debts from "./pages/Debts";
import Billing from "./pages/Billing";
import Reconciliation from "./pages/Reconciliation";
import Messages from "./pages/Messages";
import AuditCompliance from "./pages/AuditCompliance";
import LogsManagement from "./pages/Logs";
import ReinstateCenter from "./pages/ReinstateCenter";
import Layout from "./components/Layout";
import TransactionRequests from "./pages/TransactionRequests";
import Profile from "./pages/Profile";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { usePermissions } from "./context/usePermissions";
import { ChatNotificationProvider } from "./context/ChatNotificationContext";

const ProtectedRoute = ({ children, requiredModule }) => {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const { canView, loading: permLoading } = usePermissions();
  const location = useLocation();
  const isDark = theme === "dark";

  if (loading || permLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#0B0C10]" : "bg-[#F8FAFC]"}`}
      >
        <div
          className={`text-lg ${isDark ? "text-[#1FA21B]" : "text-blue-600"}`}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect exchangers away from admin routes
  if (user.role === "vendor" && location.pathname === "/dashboard") {
    return <Navigate to="/vendor-portal" replace />;
  }

  // Permission-based access control
  if (requiredModule && !canView(requiredModule)) {
    if (user.role === "vendor") {
      return <Navigate to="/vendor-portal" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Smart redirect component based on user role
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "vendor") return <Navigate to="/vendor-portal" replace />;
  if (user?.role === "sub_admin") return <Navigate to="/clients" replace />;
  return <Navigate to="/dashboard" replace />;
};

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <ChatNotificationProvider>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Full-screen chat (opens in a new tab — no app sidebar, more space) */}
      <Route
        path="/messages/full"
        element={
          <ProtectedRoute>
            <Messages fullscreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleBasedRedirect />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="transaction-requests" element={<TransactionRequests />} />
        <Route path="treasury" element={<Treasury />} />
        <Route
          path="lp-accounts"
          element={
            <ProtectedRoute requiredModule="lp_management">
              <LPAccounts />
            </ProtectedRoute>
          }
        />
        <Route path="psp" element={<PSPs />} />
        <Route
          path="vendors"
          element={
            <ProtectedRoute requiredModule="exchangers">
              <Vendors />
            </ProtectedRoute>
          }
        />
        <Route path="vendor-portal" element={<VendorDashboard />} />
        <Route path="partners" element={
            <ProtectedRoute requiredModule="partners">
              <Partners />
            </ProtectedRoute>
          } />
        <Route path="partners/:tagId" element={
            <ProtectedRoute requiredModule="partners">
              <PartnerDetail />
            </ProtectedRoute>
          } />
        <Route path="reports" element={<Reports />} />
        <Route path="daily-pnl" element={<DailyPnL />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="messages" element={<Messages />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
        <Route
          path="roles"
          element={
            <ProtectedRoute requiredModule="roles">
              <RolesPermissions />
            </ProtectedRoute>
          }
        />


        
        <Route
          path="accountant"
          element={
            <ProtectedRoute requiredModule="transactions">
              <AccountantDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/income-expenses"
          element={
            <ProtectedRoute requiredModule="income_expenses">
              <IncomeExpenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loans"
          element={
            <ProtectedRoute requiredModule="loans">
              <Loans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loans/borrower/:vendorId"
          element={
            <ProtectedRoute requiredModule="loans">
              <BorrowerDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute requiredModule="billing">
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/debts"
          element={
            <ProtectedRoute requiredModule="debts">
              <Debts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute requiredModule="audit">
              <AuditCompliance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logs"
          element={
            <ProtectedRoute requiredModule="logs">
              <LogsManagement />
            </ProtectedRoute>
          }
        />
        <Route path="reinstate" element={<ReinstateCenter />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </ChatNotificationProvider>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: isDark ? "#141414" : "#FFFFFF",
          color: isDark ? "#fff" : "#1E293B",
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid #E2E8F0",
        },
      }}
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRouter />
            <ThemedToaster />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
